import { Html5Banner } from '@cube3/cubicle/src/core/organisms/Iframes/Html5Banner';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Asset } from '@cube3/common/model/schema/resources/asset';
import { ModalNoticeUI } from '@cube3/ui/src/Modal/ModalNoticeUI';
import { HBox, VBox } from '@cube3/cubicle/src/core/templates/layout/Flex';
import { Typography } from '@cube3/cubicle/src/core/atoms/Typography';
import { useModalActions } from '../Modals/modalActions';
import { Button } from '@cube3/cubicle/src/core/atoms/Button';
import { useDispatch } from 'react-redux';
import { actionCreators } from '@cube3/state/src/redux/ducks/ui';
import { Checkbox } from '@cube3/cubicle/src/core/atoms/Checkbox/Checkbox';
import { getErrorMessage } from '@cube3/forms/src/utils/getErrorMessage';
import Loader from '@cube3/cubicle/src/core/atoms/Loader/Loader';
import { SizeVariants } from '@cube3/cubicle/src/theme/themes';
import { getEnvVar } from '../../../../utils/getEnvVar';
import { apiRoot } from '@cube3/state/src/config';
import Client from '@cube3/state/src/wrapped-cube-client';
import { useResourceList__ALPHA } from '@cube3/state/src/redux/components/Hooks/useResourceList';

interface Props {
  asset: Asset;
}

export const Html5BannerPreview = ({ asset }: Props) => {
  const { id, display_name } = asset;

  const { resources: properties, loading: loadingProperties } =
    useResourceList__ALPHA({
      resourceId: id,
      resourceType: 'asset',
      edgeType: 'properties',
      edgeLabel: 'properties'
    });

  const bannerDimension = useMemo(() => {
    if (!properties) return null;
    const bannerProperties = properties?.find(
      (prop) => prop.display_name === 'Banner Properties'
    )?.fields;

    if (bannerProperties && bannerProperties.length > 0) {
      const dimension = {
         width: Number( bannerProperties.find(i => i.key.toLowerCase() === 'width')?.value), 
         height: Number( bannerProperties.find(i => i.key.toLowerCase() === 'height')?.value)
      };

      if( !(dimension.width > 0 && dimension.height > 0) ){
          return null
      }

      return dimension;
    }
    return null;
  }, [properties]) as { width: number; height: number };

  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const envVar = getEnvVar('BANNERS_URL');

  const src = `${envVar}${apiRoot}/banners/${id}/`;

  useEffect(() => {
    if (!id) return;
    const fetchBannerData = async () => {
      try {
        await Client.auth.requestBannersAccess(
          `${envVar}${apiRoot}/auth/banners-auth-cookie/`
        );
        const response = await fetch(src, {
          credentials: 'include',
          mode: 'cors'
        });
        setLoading(false);

        if (!response.ok) {
          const data = await response.json();
          throw {
            status: response.status,
            statusText: response.statusText,
            body: data
          };
        }
      } catch (err) {
        setError(err);
      }
    };

    fetchBannerData();
  }, [src]);

  const errorMessage =
    error && error.body ? getErrorMessage(error.body.errors) : null;

  if (loadingProperties || loading || error) {
    return (
      <div
        style={{
          margin: 'auto',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}
      >
        {loading ? (
          <Loader size={SizeVariants['4xl']} />
        ) : (
          <>
            <Typography colorVariant="02">Failed to render banner:</Typography>
            <Typography colorVariant="02">{errorMessage}</Typography>
          </>
        )}
      </div>
    );
  }
  return (
    <Html5Banner src={src} title={display_name} dimension={bannerDimension} />
  );
};

export const BannerSecurityNotice = (props: {
  modalContext: { fileName: string; onOpenFile: () => void };
}) => {
  const { fileName, onOpenFile } = props.modalContext;
  const { previousModal } = useModalActions();

  const dispatch = useDispatch();
  const onChange = useCallback((val) => {
    dispatch(actionCreators.setShowBannerSecurityNotice(!val));
  }, []);

  return (
    <ModalNoticeUI
      title={`Open ${fileName || 'banner'}?`}
      onCloseEvent={previousModal}
      footerLeftComponent={
        <Button
          label="Cancel"
          buttonStyle="ghost"
          background="secondary"
          onClick={previousModal}
        />
      }
      footerRightComponent={
        <Button
          label="Open file"
          buttonStyle="solid"
          background="secondary"
          onClick={onOpenFile}
        />
      }
    >
      <VBox justify="start" gap={16}>
        <Typography>
          The HTML banner file you are about to open may contain embedded
          JavaScript. Opening this file could potentially:
        </Typography>
        <ul style={{ margin: 0, padding: '0px 16px' }}>
          <li>
            <Typography colorVariant="02">
              Execute scripts that harm or alter your website
            </Typography>
          </li>
          <li>
            <Typography colorVariant="02">
              Compromise security or user data
            </Typography>
          </li>
          <li>
            <Typography colorVariant="02">
              Interfere with normal page functionality
            </Typography>
          </li>
        </ul>
        <Typography>
          Only proceed if you trust the source of this file.
        </Typography>
        <HBox align="center" gap={16} justify="start">
          <Checkbox value={false} onChange={onChange} />
          <Typography colorVariant="02">{"Don't notify me again"}</Typography>
        </HBox>
      </VBox>
    </ModalNoticeUI>
  );
};
